import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Step1Topic from './Step1Topic';
import Step2Tone from './Step2Tone';
import Step3Frequency from './Step3Frequency';
import Step4ContentType from './Step4ContentType';
import Step5SEO from './Step5SEO';
import Step6CTA from './Step6CTA';
import Step7Audience from './Step7Audience';
import Step8Monetization from './Step8Monetization';
import Step9MediaPreferences from './Step9MediaPreferences';
import Step10ReviewPreference from './Step10ReviewPreferences';
import { Button, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import '../styles/SetupWizard.css';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setCurrentStep, updateSetupData, loadSetupData } from '../store/setupWizardSlice';
import { getApiUrl } from '../config';
import ProgressBar from './ProgressBar';
import HelpIcon from '@mui/icons-material/Help';

function SetupWizard({ onComplete }) {
  const dispatch = useDispatch();
  const { currentStep, setupData } = useSelector(state => state.setupWizard);
  const navigate = useNavigate();
  const [isImporting, setIsImporting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [stepValidity, setStepValidity] = useState({});
  const [message, setMessage] = useState('');
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserProgress = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error("Token is missing from localStorage");
          setMessage("Authentication failed. Please log in again.");
          return;
        }
  
        const response = await axios.get(`${getApiUrl()}/settings/progress`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        if (!response.data.setup_completed) {
          dispatch(loadSetupData(response.data));
          dispatch(setCurrentStep(response.data.current_step || 1));
        } else {
          onComplete();
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
        setMessage('Error fetching progress. Please try again later.');
      }
    };
  
    fetchUserProgress();
  }, [dispatch, onComplete]);

  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error("Token is missing from localStorage");
          setMessage("Authentication failed. Please log in again.");
          return;
        }
  
        const response = await fetch(`${getApiUrl()}/settings/setup/status`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
  
        const data = await response.json();
        setIsSetupComplete(data.isComplete);
        
        if (data.isComplete && data.isAuthenticated) {
          onComplete();
        }
      } catch (error) {
        console.error('Error checking setup status:', error);
      }
    };
    
    checkSetupStatus();
  }, []);

  // Helper function to generate initial topics based on user settings
  const generateInitialTopics = (settings) => {
    const mainTopic = settings.blog_topic;
    // Generate 3-5 subtopics based on the main topic
    return [
      `${mainTopic} getting started guide`,
      `Top 10 tips for ${mainTopic}`,
      `Essential ${mainTopic} basics`,
      `How to improve your ${mainTopic} skills`,
      `${mainTopic} best practices`
    ];
  };

  // Helper function to calculate next post date based on frequency
  const calculateNextPostDate = (frequency) => {
    const date = new Date();
    if (frequency === 'all') {
      date.setDate(date.getDate() + 1); // Next day
    } else {
      // Add 2-3 days between posts for initial schedule
      date.setDate(date.getDate() + Math.floor(Math.random() * 2) + 2);
    }
    return date.toISOString();
  };

  // Handle changes in the form data for each step
  const handleChange = (updatedStepData) => {
    dispatch(updateSetupData(updatedStepData));
  };

  // Function to handle saving data for each step to the backend
  const saveCurrentStep = async (completed = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error("Token is missing from localStorage");
        setMessage("Authentication failed. Please log in again.");
        return;
      }
  
      // Format seoFocus as a string if it's an array
      const seoFocusFormatted = Array.isArray(setupData.seoFocus) 
        ? setupData.seoFocus.join(',') 
        : setupData.seoFocus;

      // Format monetization strategy as a string if it's an array
      const monetizationStrategyFormatted = Array.isArray(setupData.monetizationStrategy)
        ? setupData.monetizationStrategy.join(',')
        : setupData.monetizationStrategy;

      // Format media preferences as a string if it's an array
      const mediaPreferencesFormatted = Array.isArray(setupData.mediaPreferences)
        ? setupData.mediaPreferences.join(',')
        : setupData.mediaPreferences;

      // Ensure blog_domain is properly formatted
      const blogDomain = setupData.blogDomain ? setupData.blogDomain.trim() : '';

      const backendData = {
        topic: setupData.topic || '',
        writing_tone: setupData.tone || '',
        posting_frequency: setupData.frequency || '',
        content_type: setupData.contentType || '[]',
        content_length: setupData.contentLength || '',
        seo_focus: seoFocusFormatted || '',
        cta_text: setupData.cta?.text || '',
        cta_button_color: setupData.cta?.button_color || '',
        cta_text_color: setupData.cta?.text_color || '',
        cta_url: setupData.cta?.url || '',
        target_audience: setupData.targetAudience || '',
        monetization_strategy: monetizationStrategyFormatted || '',
        amazon_tag: setupData.amazonTag || '',
        adsense_id: setupData.adsenseId || '',
        media_preferences: mediaPreferencesFormatted || '',
        review_preference: setupData.reviewPreference ?? false,
        has_existing_blog: setupData.hasExistingBlog ?? false,
        blog_domain: blogDomain,
        cms_platform: setupData.cmsPlatform || '',
        step: currentStep,
        setup_completed: completed
      };

      console.log('Saving data to backend:', backendData); // Add logging

      const response = await axios.post(
        `${getApiUrl()}/settings/progress`,
        backendData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('Server response:', response.data); // Add logging
      console.log('Data saved successfully for step:', currentStep);
    } catch (error) {
      console.error('Error saving data:', error);
      if (error.response) {
        console.error('Server error response:', error.response.data); // Add logging
        setMessage(`Error: ${error.response.data.error || 'Unable to save data.'}`);
      } else if (error.request) {
        setMessage('Error: No response from the server. Please check your network connection.');
      } else {
        setMessage('Error: Something went wrong. Please try again.');
      }
    }
  };

  // Function to handle moving to the next step
  const handleNext = async () => {
    if (isImporting) {
      return;
    }

    if (currentStep >= 10) { // Last step
      try {
        setIsGenerating(true);
        setGenerationProgress('Saving your preferences...');
        await saveCurrentStep(true);
        
        setGenerationProgress('Starting to generate your content...');
        const token = localStorage.getItem('token');
        await axios.post(
          `${getApiUrl()}/posts/generate-initial-posts`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        // Show brief success message
        setGenerationProgress("Content generation started! You'll see progress in the dashboard.");
        
        // Wait just a moment to show the message
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Redirect to dashboard
        navigate('/dashboard');
        
      } catch (error) {
        console.error('Error completing setup:', error);
        setMessage(error.response?.data?.message || error.message || 'Error initiating content generation. Please try again.');
        setIsGenerating(false);
        setGenerationProgress('');
        return;
      }
    } else {
      await saveCurrentStep();
      dispatch(setCurrentStep(currentStep + 1));
    }
  };

  // Function to handle moving to the previous step
  const handlePrevious = () => {
    dispatch(setCurrentStep(currentStep - 1));
  };

  // Logout function to clear state and redirect to landing page
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('setupCompleted');
    navigate('/');
  };

  const handleStepValidityChange = (step, isValid) => {
    setStepValidity(prev => ({
      ...prev,
      [step]: isValid
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        // Require a valid topic AND either no existing blog OR successful import
        return setupData.topic?.trim().length >= 3 && 
               (!setupData.hasExistingBlog || 
                (setupData.hasExistingBlog && setupData.importSuccess));
      case 2:
        // Require at least one tone to be selected
        return stepValidity[2] === true;
      case 3:
        // Require valid frequency selection
        return stepValidity[3] === true;
      case 4:
        return stepValidity[4] === true;
      case 5:
        // Require at least one SEO strategy
        return stepValidity[5] === true;
      case 6:
        // Require CTA text if CTA is enabled
        return stepValidity[6] === true;
      case 7:
        // Require at least one target audience
        return stepValidity[7] === true;
      default:
        return true;
    }
  };

  const showHelp = () => {
    setHelpOpen(true);
  };

  const handleCloseHelp = () => {
    setHelpOpen(false);
  };

  // Render the appropriate step component
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1Topic
            hasExistingBlog={setupData.hasExistingBlog}
            blogDomain={setupData.blogDomain}
            cmsPlatform={setupData.cmsPlatform}
            blogTopic={setupData.topic}
            onChange={(data) => {
              handleChange(data);
              handleStepValidityChange(1, !data.hasExistingBlog || data.importSuccess);
            }}
            onImportStart={() => setIsImporting(true)}
            onImportEnd={() => setIsImporting(false)}
          />
        );
      case 2:
        return (
          <Step2Tone
            tone={setupData.tone}
            onChange={(data) => {
              handleChange({ tone: data.tone });
              handleStepValidityChange(2, data.isValid);
            }}
            nextStep={handleNext}
            prevStep={handlePrevious}
          />
        );        
      case 3:
        return (
          <Step3Frequency
            frequency={setupData.frequency}
            onChange={(data) => {
              handleChange({ frequency: data.frequency });
              handleStepValidityChange(3, data.isValid);
            }}
          />
        );
      case 4:
        return (
          <Step4ContentType
            contentType={setupData.contentType}
            contentLength={setupData.contentLength}
            onChange={(data) => handleChange(data)}
            onValidityChange={(isValid) => handleStepValidityChange(4, isValid)}
          />
        );
      case 5:
        return (
          <Step5SEO
            seoFocus={setupData.seoFocus}
            onChange={(data) => {
              handleChange({ seoFocus: data.seoFocus });
              handleStepValidityChange(5, data.isValid);
            }}
          />
        );
      case 6:
        return (
          <Step6CTA
            cta={setupData.cta}
            onChange={(data) => {
              handleChange({ cta: data });
              handleStepValidityChange(6, data.isValid);
            }}
          />
        );
      case 7:
        return (
          <Step7Audience
            targetAudience={setupData.targetAudience}
            onChange={(data) => {
              handleChange({ targetAudience: data.targetAudience });
              handleStepValidityChange(7, data.isValid);
            }}
          />
        );
      case 8:
        return (
          <Step8Monetization
            monetizationStrategy={setupData.monetizationStrategy}
            amazonTag={setupData.amazonTag}
            adsenseId={setupData.adsenseId}
            onChange={handleChange}
          />
        );
      case 9:
        return (
          <Step9MediaPreferences
            mediaPreferences={setupData.mediaPreferences}
            onChange={(data) => handleChange({ mediaPreferences: data })}
          />
        );        
      case 10:
        return (
          <Step10ReviewPreference
            reviewPreference={setupData.reviewPreference}
            onChange={(data) => handleChange({ reviewPreference: data })}
          />
        );
      default:
        return <div>Setup Complete!</div>;
    }
  };

  return (
    <div className="wizard-container">
      <div className="wizard-header">
        <h2 className="wizard-title">Setup Wizard</h2>
        <Button 
          variant="outlined" 
          color="secondary" 
          onClick={handleLogout}
          sx={{ position: 'absolute', top: 20, right: 20 }}
        >
          Logout
        </Button>
      </div>

      <p className="wizard-subtitle">Complete the setup to configure your blog</p>
      <ProgressBar 
        currentStep={currentStep} 
        totalSteps={10}  // Total number of steps in the wizard
      />
      <div className="wizard-step">
        <Tooltip
          content="Need help? Click here for step-by-step guide"
          position="right"
        >
          <HelpIcon onClick={showHelp} />
        </Tooltip>
        {renderStep()}
      </div>
      {message && <p style={{ color: 'red' }}>{message}</p>}

      <div className="wizard-footer">
        {currentStep > 1 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handlePrevious}
            sx={{ marginRight: 2 }}
            disabled={isImporting || isGenerating}
          >
            Back
          </Button>
        )}
        <Button 
          variant="contained" 
          color="success" 
          onClick={handleNext}
          disabled={isImporting || isGenerating || !canProceed()}
          endIcon={isGenerating && <CircularProgress size={20} color="inherit" />}
          className={!canProceed() ? 'button-disabled' : ''}
        >
          {currentStep < 10 ? 'Next' : (isGenerating ? 'Generating...' : 'Finish')}
        </Button>
      </div>
      
      {generationProgress && (
        <div className="generation-progress">
          <CircularProgress size={24} sx={{ marginRight: 2 }} />
          <span>{generationProgress}</span>
        </div>
      )}

      <Dialog open={helpOpen} onClose={handleCloseHelp} maxWidth="md">
        <DialogTitle>Setup Guide</DialogTitle>
        <DialogContent>
          <div className="help-content">
            <h3>Step {currentStep}: {getStepTitle(currentStep)}</h3>
            <p>{getStepDescription(currentStep)}</p>
            <ul>
              {getStepInstructions(currentStep).map((instruction, index) => (
                <li key={index}>{instruction}</li>
              ))}
            </ul>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHelp} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <div className="error-message" style={{ color: 'red', marginTop: '10px' }}>
          {error}
        </div>
      )}
    </div>
  );
}

const getStepTitle = (step) => {
  switch (step) {
    case 1:
      return "Blog Topic";
    case 2:
      return "Writing Tone";
    case 3:
      return "Posting Frequency";
    case 4:
      return "Content Type";
    case 5:
      return "SEO Focus";
    case 6:
      return "Call to Action";
    case 7:
      return "Target Audience";
    case 8:
      return "Monetization";
    case 9:
      return "Media Preferences";
    case 10:
      return "Review Preferences";
    default:
      return "";
  }
};

const getStepDescription = (step) => {
  switch (step) {
    case 1:
      return "Choose the main topic or niche for your blog. This will help us generate relevant content.";
    case 2:
      return "Select the writing style that best matches your brand voice.";
    case 3:
      return "Set how often you want new content to be published.";
    case 4:
      return "Choose the types of content you want to create and their typical length.";
    case 5:
      return "Define your SEO strategy and target keywords.";
    case 6:
      return "Set up your call-to-action preferences for blog posts.";
    case 7:
      return "Define your target audience to better tailor content.";
    case 8:
      return "Choose how you plan to monetize your blog.";
    case 9:
      return "Set your preferences for images and media in blog posts.";
    case 10:
      return "Configure your content review preferences.";
    default:
      return "";
  }
};

const getStepInstructions = (step) => {
  switch (step) {
    case 1:
      return [
        "Enter your blog's main topic or niche",
        "If you have an existing blog, provide its URL",
        "Select your content management system if applicable"
      ];
    case 2:
      return [
        "Choose a primary writing tone",
        "You can combine tones for a unique voice",
        "Consider your audience when selecting the tone"
      ];
    // Add instructions for other steps...
    default:
      return [];
  }
};

export default SetupWizard;
